import { getService } from 'reducers/service';
import ServiceName from '../../shared/constants/serviceName';
import { getPersonById, getPlaceById } from '../../shared/services/generalServices';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getPersonImages = async (personId, isMarkOnly) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.get(personId, { query: { IsMarkOnly: isMarkOnly } });
};

export const editPersonImage = async (imageId, data) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.patch(imageId, data);
};

export const deletePersonImage = async (imageId) => {
  const service = getService(ServiceName.CadPersonPhoto);
  return service.remove(imageId);
};

export const uploadPersonPhoto = async ({ file, ...rest }) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  const fileName = file?.name ? file.name.substring(0, 49) : 'Snapshot';
  const fileType = file?.name ? file.name.split('.').pop() : 'png';
  const data = await readFileAsBase64(file);

  return service.create({ ...rest, fileName, fileType, data });
};

export const getPersonImage = async (imageId) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  return service.get(imageId);
};

export const createPersonMark = async (data) => {
  const service = getService(ServiceName.CadUploadPersonPhoto);
  return service.create(data);
};

export const getPersonBulletins = async (personId) => {
  const service = getService(ServiceName.CadAlerts);
  return service.find({ query: { ptsPersonID: personId } });
};

export const getBulletin = async (alertId) => {
  const service = getService(ServiceName.CadSearchAlerts);
  return service.get(alertId);
};

export const createBulletin = async (data) => {
  const service = getService(ServiceName.CadSearchAlerts);
  return service.create(data);
};

export const updateBulletin = async (alertId, data) => {
  const service = getService(ServiceName.CadSearchAlerts);
  return service.update(alertId, data);
};

export const deleteBulletin = async (alertId) => {
  const service = getService(ServiceName.CadSearchAlerts);
  return service.remove(alertId);
};

export const getPersonRecordDetails = async (personId, excludeEmployeeRelated) => {
  const service = getService(ServiceName.SearchPersonRelatedRecord);
  return service.get(personId, { query: { excludeEmployeeRelated } });
};

export const getPeopleSops = async (personId) => {
  const service = getService(ServiceName.RecordPersonSops);
  const response = await service.find({
    query: {
      IsDeleted: false,
      ptsParentID: personId,
      ParentType: 'Person',
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const getApprovedSops = async () => {
  const service = getService(ServiceName.PtsSops);
  const response = await service.find({
    query: {
      SOPID: { $ne: null },
      $limit: 1000,
      IsApproved: true,
      IsDeleted: false,
    },
  });
  return response?.data || response || [];
};

export const savePersonSop = async (payload) => {
  const service = getService(ServiceName.RecordPersonSops);
  return service.create(payload);
};

export const deletePersonSop = async (query) => {
  const service = getService(ServiceName.RmsAssignedSops);
  return service.remove(query);
};

export const getPersonDetails = async (ptsPersonID) => {
  const service = getService(ServiceName.ptsPerson);

  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
    },
  });

  return response?.data || [];
};

export const createPerson = async (payload) => {
  const service = getService(ServiceName.CadSearchPeople);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPerson = async (ptsPersonID, payload) => {
  const service = getService(ServiceName.CadSearchPeople);
  const response = await service.patch(ptsPersonID, payload);
  return response?.data || response;
};

export const createAddress = async (payload) => {
  const service = getService(ServiceName.CadHistoryEditAddress);
  const response = await service.create(payload);
  return response?.data || response;
};

export const getPeopleNames = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonName);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonName = async (payload) => {
  const service = getService(ServiceName.RecordPersonName);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonName = async (ptsNameID, payload) => {
  const service = getService(ServiceName.RecordPersonName);
  const response = await service.patch(ptsNameID, payload);
  return response?.data || response;
};

export const deletePersonName = async (ptsNameID) => {
  const service = getService(ServiceName.RecordPersonName);
  const response = await service.remove(ptsNameID);
  return response?.data || response;
};

export const getPeopleContactMethods = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.find({
    query: {
      ptsParentID: ptsPersonID,
      PartyType: 'Person',
      IsDeleted: false,
      Info: { $ne: '' },
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonContact = async (payload) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonContact = async (ptsInfoID, payload) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.patch(ptsInfoID, payload);
  return response?.data || response;
};

export const deletePersonContact = async (ptsInfoID) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.remove(ptsInfoID);
  return response?.data || response;
};

export const getPeopleCredentials = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonCredentials);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonCredential = async (payload) => {
  const service = getService(ServiceName.RecordPersonCredentials);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonCredential = async (ptsCredentialID, payload) => {
  const service = getService(ServiceName.RecordPersonCredentials);
  const response = await service.patch(ptsCredentialID, payload);
  return response?.data || response;
};

export const deletePersonCredential = async (ptsCredentialID) => {
  const service = getService(ServiceName.RecordPersonCredentials);
  const response = await service.remove(ptsCredentialID);
  return response?.data || response;
};

export const getPeopleEmployments = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonEmployment);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  const rows = response?.data || response || [];

  return rows.map((row) => {
    const place = Array.isArray(row.Place) && row.Place.length ? row.Place[0] : null;
    return {
      ...row,
      PlaceName: place?.PlaceName || null,
      PlaceType: place?.PlaceType || null,
      rowKey: `${row.ptsPersonID}-${row.ptsPlaceID}`,
    };
  });
};

export const createPersonEmployment = async (payload) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonEmployment = async (compositeKey, payload) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.patch(compositeKey, payload);
  return response?.data || response;
};

export const deletePersonEmployment = async (compositeKey) => {
  const service = getService(ServiceName.CadSearchPeopleEmployment);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getPeopleFeatures = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonFeature);
  const response = await service.find({
    query: {
      ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createPersonFeature = async (payload) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editPersonFeature = async (ptsFeatureID, payload) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.patch(ptsFeatureID, payload);
  return response?.data || response;
};

export const deletePersonFeature = async (ptsFeatureID) => {
  const service = getService(ServiceName.CadPersonFeatures);
  const response = await service.remove(ptsFeatureID);
  return response?.data || response;
};

export const getPersonDnaSamples = async (ptsPersonID) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const samples = (await service.get(ptsPersonID)) || [];

  return Promise.all(
    samples.map(async (sample) => {
      const [crimeLabPlace, takenByPerson] = await Promise.all([
        sample.ptsCrimeLabPlaceID ? getPlaceById(sample.ptsCrimeLabPlaceID) : null,
        sample.ptsTakenByPersonID ? getPersonById(sample.ptsTakenByPersonID) : null,
      ]);

      return {
        ...sample,
        CrimeLab: crimeLabPlace?.PlaceName || null,
        TakenBy: takenByPerson?.FullName || null,
      };
    })
  );
};

export const createDnaSample = async (payload) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.create(payload);
  return response?.data || response;
};

export const editDnaSample = async (ptsDNASampleID, payload) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.patch(ptsDNASampleID, payload);
  return response?.data || response;
};

export const deleteDnaSample = async (ptsDNASampleID) => {
  const service = getService(ServiceName.CadPersonDnaSamples);
  const response = await service.remove(ptsDNASampleID);
  return response?.data || response;
};

export const getPeopleAssociates = async (ptsPersonID) => {
  const service = getService(ServiceName.CadPersonAssociate);
  const response = await service.find({ query: { ptsPersonID } });
  return response?.data || response || [];
};

export const createPersonAssociate = async (payload) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updatePersonAssociate = async (compositeKey, payload) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.update(compositeKey, payload);
  return response?.data || response;
};

export const deletePersonAssociate = async (compositeKey) => {
  const service = getService(ServiceName.RecordPersonAssociates);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getPeopleVehicleRegistrations = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonVehicleRegistration);
  const response = await service.find({
    query: {
      ptsParentID: ptsPersonID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const createVehicleRegistration = async (payload) => {
  const service = getService(ServiceName.CadPtsVehicleReg);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updateVehicleRegistration = async (ptsRegistrationID, payload) => {
  const service = getService(ServiceName.CadPtsVehicleReg);
  const response = await service.patch(ptsRegistrationID, payload);
  return response?.data || response;
};

export const deleteVehicleRegistration = async (ptsRegistrationID) => {
  const service = getService(ServiceName.CadPtsVehicleReg);
  const response = await service.remove(ptsRegistrationID);
  return response?.data || response;
};

export const createVehicle = async (payload) => {
  const service = getService(ServiceName.CadSearchVehicles);
  const response = await service.create(payload);
  return response?.data || response;
};

export const getVehicleDetails = async (ptsVehicleID) => {
  const service = getService(ServiceName.CadPtsVehicle);
  const response = await service.get(ptsVehicleID);
  const rows = response?.data || response || [];
  return Array.isArray(rows) ? rows[0] : rows;
};

export const updateVehicleDetails = async (ptsVehicleID, payload) => {
  const service = getService(ServiceName.CadPtsVehicle);
  const response = await service.patch(ptsVehicleID, payload);
  return response?.data || response;
};

export const getVehicleTowingRecords = async (ptsVehicleID) => {
  const service = getService(ServiceName.CadVehicleTowing);
  const response = await service.find({ query: { ptsVehicleID } });
  return response?.data || response || [];
};

export const createPlace = async (payload) => {
  const service = getService(ServiceName.CadSearchPlaces);
  const response = await service.create(payload);
  return response?.data || response;
};

export const searchPlaceAddresses = async (ptsPlaceID) => {
  const service = getService(ServiceName.CadSearchPlaceAddress);
  const response = await service.find({
    query: { ptsPlaceID, IsDeleted: false },
  });
  const rows = response?.data || response || [];
  return rows.map((row) => row.Address).filter(Boolean);
};

export const getAuthorityAddressById = async (ptsAddressID) => {
  const service = getService(ServiceName.CadSearchAddresses);
  return service.get(ptsAddressID);
};

// Matches legacy's PlaceEdit/index.js getPlace(): the `places` service eager-loads
// PlaceAddress (+ Address + City), Employement, and ContactInfo alongside the raw ptsPlace row.
export const getPlaceDetails = async (ptsPlaceID) => {
  const service = getService(ServiceName.Places);
  return service.get(ptsPlaceID);
};

// Matches legacy's AddPlace.js updatePlace(): cad-search-places.patch only ever processes
// `data.Place` server-side — Addresses/Contacts sent alongside it are silently ignored.
export const updatePlaceDetails = async (ptsPlaceID, payload) => {
  const service = getService(ServiceName.CadSearchPlaces);
  const response = await service.patch(ptsPlaceID, { Place: payload });
  return response?.data || response;
};

export const getPlaceAddresses = async (ptsPlaceID) => {
  const service = getService(ServiceName.Places);
  const response = await service.get(ptsPlaceID);
  const placeAddresses = response?.PlaceAddress || [];

  return placeAddresses.map((placeAddress) => ({
    ...placeAddress.Address,
    ...placeAddress,
    City: placeAddress.Address?.City?.CityDescription || null,
  }));
};

export const createPlaceAddressLink = async (payload) => {
  const service = getService(ServiceName.CadSearchPlaceAddress);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updatePlaceAddressLink = async (compositeKey, payload) => {
  const service = getService(ServiceName.CadSearchPlaceAddress);
  const response = await service.patch(null, payload, { query: compositeKey });
  return response?.data || response;
};

export const deletePlaceAddressLink = async (compositeKey) => {
  const service = getService(ServiceName.CadPlaceAddress);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getPlaceContacts = async (ptsPlaceID) => {
  const service = getService(ServiceName.RecordPersonContact);
  const response = await service.find({
    query: {
      ptsParentID: ptsPlaceID,
      PartyType: 'Place',
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const getPlaceEmployees = async (ptsPlaceID) => {
  const service = getService(ServiceName.RecordPersonEmployment);
  const response = await service.find({
    query: {
      ptsPlaceID,
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  const rows = response?.data || response || [];

  return rows.map((row) => {
    const person =
      Array.isArray(row.PersonInfo) && row.PersonInfo.length ? row.PersonInfo[0] : null;
    return {
      ...row,
      FirstName: person?.FirstName || null,
      LastName: person?.LastName || null,
      FullName: person?.FullName || null,
      rowKey: `${row.ptsPersonID}-${row.ptsPlaceID}`,
    };
  });
};

// Matches legacy's PlacesReducer.js upsertPlaceEmployment(): find the existing (possibly
// soft-deleted) {ptsPersonID, ptsPlaceID} row first — update/undelete it if found, else create.
export const savePlaceEmployment = async (payload) => {
  const service = getService(ServiceName.RecordPersonEmployment);
  const { ptsPersonID, ptsPlaceID } = payload;
  const existing = await service.find({ query: { ptsPersonID, ptsPlaceID } });
  const rows = existing?.data || existing || [];

  if (rows.length) {
    return service.update({ ptsPersonID, ptsPlaceID }, { ...payload, IsDeleted: false });
  }

  return service.create(payload);
};

export const deletePlaceEmployment = async (compositeKey) => {
  const service = getService(ServiceName.RecordPersonEmployment);
  return service.remove(null, { query: compositeKey });
};

export const getPlaceSops = async (ptsPlaceID) => {
  const service = getService(ServiceName.RecordPersonSops);
  const response = await service.find({
    query: {
      IsDeleted: false,
      ptsParentID: ptsPlaceID,
      ParentType: 'Place',
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const getPlaceVehicleRegistrations = async (ptsPlaceID) => {
  const service = getService(ServiceName.PtsVehicleReg);
  const response = await service.find({
    query: {
      ptsParentID: ptsPlaceID,
      PartyType: 'Place',
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const getPeopleAddresses = async (ptsPersonID) => {
  const service = getService(ServiceName.RecordPersonAddress);
  const response = await service.find({ query: { ptsPersonID } });
  const rows = response?.data || response || [];

  return rows.map((row) => {
    const address = Array.isArray(row.Address) && row.Address.length ? row.Address[0] : null;
    return {
      ...address,
      ptsPersonID: row.ptsPersonID,
      ptsAddressID: row.ptsAddressID,
      IsPrimary: row.IsPrimary,
      City: address?.City?.CityDescription || null,
      Created: row.Created,
      CreatedBy: row.CreatedBy,
      Updated: row.Updated,
      UpdatedBy: row.UpdatedBy,
    };
  });
};

export const createPersonAddressLink = async (payload) => {
  const service = getService(ServiceName.RecordPersonAddress);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updatePersonAddressLink = async (compositeKey, payload) => {
  const service = getService(ServiceName.RecordPersonAddress);
  const response = await service.update(compositeKey, payload);
  return response?.data || response;
};

export const deletePersonAddressLink = async (compositeKey) => {
  const service = getService(ServiceName.RecordPersonAddress);
  const response = await service.remove(compositeKey);
  return response?.data || response;
};

export const getAddressDetails = async (ptsAddressID) => {
  const service = getService(ServiceName.ptsAddress);
  return service.get(ptsAddressID);
};

export const getPeopleTags = async (ptsPersonID) => {
  const service = getService(ServiceName.RmsTagsV2);
  const response = await service.find({
    query: {
      ptsParentID: ptsPersonID,
      ParentType: 'Person',
      IsDeleted: false,
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};

export const savePersonTag = async (payload) => {
  const service = getService(ServiceName.RmsTagsV2);
  if (payload.ptsTagID) {
    return service.update({ ptsTagID: payload.ptsTagID, IsDeleted: false }, payload);
  }
  return service.create(payload);
};

export const removePersonTag = async (ptsTagID) => {
  const service = getService(ServiceName.RmsTagsV2);
  return service.remove(null, { query: { ptsTagID, IsDeleted: false } });
};

export const getAddressDuplicateCheckFields = async () => {
  const service = getService(ServiceName.Cdr);
  const response = await service.find({
    query: {
      CDRKey: 'AddDuplicate',
      ValueBoolean: true,
    },
  });
  return response?.data || response || [];
};

export const findAddressDuplicates = async (query) => {
  const service = getService(ServiceName.RmsAddressDuplicates);
  const response = await service.find({ query });
  return response?.data || response || [];
};

export const getCities = async () => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.find({ query: { $limit: 1000 } });
  return response?.data || response || [];
};

export const createCityRecord = async (payload) => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.create(payload);
  return response?.data || response;
};

export const updateCityRecord = async (ptsCityID, payload) => {
  const service = getService(ServiceName.CityManagement);
  const response = await service.patch(ptsCityID, payload);
  return response?.data || response;
};

export const deleteCityRecord = async (ptsCityID) => {
  const service = getService(ServiceName.CityManagement);
  return service.remove(ptsCityID);
};

export const getAddressLookup = async (id) => {
  const service = getService(ServiceName.Address_lookup);
  const response = await service.get(id);
  return response || {};
};

// ---- Address sub-tabs (Coordinates, Zones, History, Parties, Pre-Plan, SOPs) ----

export const getAddressCoordinateId = async (ptsAddressID) => {
  const service = getService(ServiceName.CadAddressPtsCoordinateID);
  const response = await service.get(ptsAddressID);
  return response?.ptsCoordinateID || null;
};

export const getAddressCoordinateDetails = async (ptsCoordinateID) => {
  const service = getService(ServiceName.CadAddressCoordinates);
  return service.get(ptsCoordinateID);
};

// Legacy's own save flow: create()/upsert the coordinate row, then patch the resulting
// ptsCoordinateID back onto the address row itself.
export const saveAddressCoordinates = async (payload) => {
  const service = getService(ServiceName.CadAddressCoordinates);
  return service.create(payload);
};

export const linkAddressCoordinate = async (ptsAddressID, ptsCoordinateID) => {
  const service = getService(ServiceName.CadAddressCoordinates);
  return service.patch(ptsAddressID, { ptsCoordinateID });
};

export const getAddressZones = async ({ ptsAddressID, ptsCoordinateID, coords }) => {
  const service = getService(ServiceName.Cad);
  const response = await service.get({
    type: 'address-zones',
    data: { ptsAddressID, ptsCoordinateID, coords },
  });
  return response || [];
};

export const createAddressZone = async (payload) => {
  const service = getService(ServiceName.PtsZoneInfo);
  return service.create(payload);
};

export const deleteAddressZone = async (ptsZoneInfoID) => {
  const service = getService(ServiceName.PtsZoneInfo);
  return service.remove(ptsZoneInfoID);
};

export const getAddressHistory = async (ptsAddressID) => {
  const service = getService(ServiceName.CadAddressHistory);
  const response = await service.find({ query: { ptsAddressID } });
  return response?.data || response || [];
};

export const getAddressParties = async (ptsAddressID) => {
  const service = getService(ServiceName.CadAddressParties);
  const response = await service.find({ query: { ptsAddressID } });
  return response?.data || response || [];
};

export const saveAddressParty = async (payload) => {
  const service = getService(ServiceName.CadAddressParties);
  return service.create(payload);
};

export const deleteAddressParty = async (ROWGUID) => {
  const service = getService(ServiceName.CadAddressParties);
  return service.remove(ROWGUID);
};

export const getPersonActiveWarrants = async (ptsPersonID) => {
  const service = getService(ServiceName.CadSearchWarrants);
  return service.get(ptsPersonID);
};

export const getAddressPrePlan = async (ptsAddressID) => {
  const service = getService(ServiceName.CadAddressPrePlan);
  return service.get(ptsAddressID);
};

export const saveAddressPrePlan = async (payload) => {
  const service = getService(ServiceName.CadAddressPrePlan);
  return service.create(payload);
};

export const getPrePlanInspections = async (ptsAddressID) => {
  const service = getService(ServiceName.CadAddressPrePlanInspections);
  const response = await service.get(ptsAddressID);
  return response || [];
};

export const savePrePlanInspection = async (payload) => {
  const service = getService(ServiceName.CadAddressPrePlanInspections);
  return service.create(payload);
};

export const deletePrePlanInspection = async (id) => {
  const service = getService(ServiceName.CadAddressPrePlanInspections);
  return service.remove(id);
};

export const getAddressSops = async (ptsAddressID) => {
  const service = getService(ServiceName.RecordPersonSops);
  const response = await service.find({
    query: {
      IsDeleted: false,
      ptsParentID: ptsAddressID,
      ParentType: 'Address',
      $sort: { Created: -1 },
    },
  });
  return response?.data || response || [];
};
